import { useEffect, useRef, useState } from 'react';
import { GlobalAPIError, getGeneratedDocument } from '@shory-health-individual/customer-api';
import { GeneratedDocumentTypes, PlanCategory, PolicyType } from '../../../common';
import { useAppPlatformContextOptions } from '../../../contexts/app';
import { JourneyDetails } from '../../../server';
import { useSystemConfigurationSettings } from '../../app-init-data';
import useMemberDocumentData from './useMemberDocumentData';

type UseMembersData = {
	journey?: JourneyDetails;
};

export type MemberData = {
	maritalStatusId: number;
	genderId: number;
	dateOfBirth: string;
	nationalityId: number;
	salaryBandId: number;
	passportNo: string;
	emiratesIdNumber: string;
	visaFileNumber: string;
	emirateId: number;
	firstName: string;
	lastName: string;
	relationshipTypeId: number;
	memberId: string;
	isPrincipal: boolean;
	unifiedNumber: string;
	fileUrl: string;
	nationality: string;
	gender: string;
	relationship?: string;
	salaryBand?: string;
	maritalStatus?: string;
	emirate?: string;
	occupation?: string;
};

export const useMembersData = ({ journey }: UseMembersData) => {
	const data: Partial<MemberData>[] = [
		{ ...(journey?.principalDetails ?? {}), isPrincipal: true },
		...(journey?.dependents ?? []),
	];
	const { onTransformData } = useMemberDocumentData();
	const { apiConnectionOptions, onGeneralErrorHandling } = useAppPlatformContextOptions();
	const { systemConfigurationSettings } = useSystemConfigurationSettings();
	const memberRef = useRef<{ memberInfo: Partial<MemberData>; fileUrl: string }[]>(onTransformData(data));
	const [isDisabled, setIsDisabled] = useState(true);
	const intervalsRef = useRef<{ intervalId?: NodeJS.Timer; timeoutId?: NodeJS.Timer }>({});

	const refreshMemberData = () => {
		const currentData = data?.map((d) => {
			return {
				...d,
				fileUrl: memberRef.current?.find((m) => m.memberInfo.memberId === d.memberId)?.fileUrl,
			};
		});

		return onTransformData(currentData);
	};

	const isFileVisible = (isPrincipal?: boolean) => {
		if (journey?.planCategoryIdForSelectedPlan === PlanCategory.Basic) return false;

		if (!isPrincipal) return true;

		return !!(journey?.policyType && [PolicyType.OnlyMe, PolicyType.MeAndFamily].includes(journey.policyType));
	};

	const documentRefreshTimeOutInMilliSeconds =
		systemConfigurationSettings?.documentRefreshTimeOutInMilliSeconds ?? 100000;
	const documentRefreshIntervalTimeInInMilliSeconds =
		systemConfigurationSettings?.documentRefreshIntervalTimeInInMilliSeconds ?? 5000;

	const onGetGeneratedDocument = async () => {
		try {
			const { documents } = await getGeneratedDocument(apiConnectionOptions)({
				parameters: {
					encryptedJourneyId: journey?.encryptedJourneyId ?? '',
					documentType: GeneratedDocumentTypes.MedicalDeclaration,
				},
			});

			const newData = memberRef.current.map((member) => ({
				...member,
				fileUrl: documents.find((d) => d.memberId === member.memberInfo.memberId)?.documentUrl ?? '',
			}));

			const isSomeDocumentsMissing = newData.some(
				(member) => !member.fileUrl && isFileVisible(member.memberInfo.isPrincipal)
			);

			memberRef.current = newData;

			if (!isSomeDocumentsMissing) {
				setIsDisabled(false);
				onClearIntervals();
			}
		} catch (e) {
			onGeneralErrorHandling(e as GlobalAPIError);
		}
	};

	function onClearIntervals() {
		clearInterval(intervalsRef.current.intervalId);
		clearTimeout(intervalsRef.current.timeoutId);
	}

	useEffect(() => {
		const intervalId = setInterval(() => {
			onGetGeneratedDocument();
		}, documentRefreshIntervalTimeInInMilliSeconds);

		const timeoutId = setTimeout(() => {
			onClearIntervals();
		}, documentRefreshTimeOutInMilliSeconds);

		intervalsRef.current.intervalId = intervalId;
		intervalsRef.current.timeoutId = timeoutId;
		onGetGeneratedDocument();

		return () => onClearIntervals();
	}, []);

	return {
		isDisabled,
		isFileVisible,
		refreshMemberData,
	};
};
